import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Save, AlertCircle, CheckCircle, Info, Calendar, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';

// Define all available time slots (06:00 - 22:00)
const ALL_TIME_SLOTS = [];
for (let hour = 6; hour <= 22; hour++) {
  ALL_TIME_SLOTS.push(`${hour.toString().padStart(2, '0')}:00`);
}

// Day names mapping
const DAYS = [
  { key: 'monday', label: 'Monday', labelTr: 'Pazartesi' },
  { key: 'tuesday', label: 'Tuesday', labelTr: 'Salı' },
  { key: 'wednesday', label: 'Wednesday', labelTr: 'Çarşamba' },
  { key: 'thursday', label: 'Thursday', labelTr: 'Perşembe' },
  { key: 'friday', label: 'Friday', labelTr: 'Cuma' },
  { key: 'saturday', label: 'Saturday', labelTr: 'Cumartesi' },
  { key: 'sunday', label: 'Sunday', labelTr: 'Pazar' },
];

// Default empty schedule
const getEmptySchedule = () => ({
  monday: { enabled: false, slots: [] },
  tuesday: { enabled: false, slots: [] },
  wednesday: { enabled: false, slots: [] },
  thursday: { enabled: false, slots: [] },
  friday: { enabled: false, slots: [] },
  saturday: { enabled: false, slots: [] },
  sunday: { enabled: false, slots: [] },
});

const AvailabilityTab = () => {
  const [weeklySchedule, setWeeklySchedule] = useState(getEmptySchedule());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSchedule, setOriginalSchedule] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const fetchAvailability = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/availability`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.weekly_schedule) {
          setWeeklySchedule(data.weekly_schedule);
          setOriginalSchedule(JSON.stringify(data.weekly_schedule));
        }
        setSummary(data.summary);
      } else {
        // If 404, use default empty schedule
        setOriginalSchedule(JSON.stringify(getEmptySchedule()));
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast({
        title: 'Error',
        description: 'Failed to load availability settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Fetch current availability on mount
  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  // Track changes
  useEffect(() => {
    if (originalSchedule) {
      const currentSchedule = JSON.stringify(weeklySchedule);
      setHasChanges(currentSchedule !== originalSchedule);
    }
  }, [weeklySchedule, originalSchedule]);

  // Toggle day enabled/disabled
  const toggleDay = (dayKey) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled: !prev[dayKey].enabled,
        // If enabling, add default work hours (09:00 - 17:00)
        slots: !prev[dayKey].enabled 
          ? ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00']
          : []
      }
    }));
  };

  // Toggle a specific time slot for a day
  const toggleSlot = (dayKey, slot) => {
    setWeeklySchedule(prev => {
      const currentSlots = prev[dayKey].slots || [];
      const newSlots = currentSlots.includes(slot)
        ? currentSlots.filter(s => s !== slot)
        : [...currentSlots, slot].sort();
      
      return {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          slots: newSlots,
          // Keep the day enabled even if no slots selected
          enabled: prev[dayKey].enabled
        }
      };
    });
  };

  // Select all slots for a day
  const selectAllSlots = (dayKey) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayKey]: {
        enabled: true,
        slots: [...ALL_TIME_SLOTS]
      }
    }));
  };

  // Clear all slots for a day (keeps day enabled, just clears selected hours)
  const clearAllSlots = (dayKey) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayKey]: {
        enabled: true,  // Keep the day enabled
        slots: []       // Just clear the selected slots
      }
    }));
  };

  // Save availability
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/api/trainer/availability`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          weekly_schedule: weeklySchedule,
          special_dates: [],
          session_duration: 60,
          buffer_time: 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSummary(data.summary);
        setOriginalSchedule(JSON.stringify(weeklySchedule));
        setHasChanges(false);
        
        toast({
          title: 'Saved!',
          description: 'Your availability has been updated successfully.',
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving availability:', error);
      toast({
        title: 'Error',
        description: 'Failed to save availability settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading availability...</span>
      </div>
    );
  }

  // Calculate active days count
  const activeDaysCount = Object.values(weeklySchedule).filter(d => d.enabled && d.slots?.length > 0).length;
  const totalSlotsCount = Object.values(weeklySchedule).reduce((sum, d) => sum + (d.slots?.length || 0), 0);

  // Calculate working hours per day with consecutive time ranges
  const getWorkingHoursPerDay = () => {
    const workingDays = [];
    DAYS.forEach(day => {
      const dayData = weeklySchedule[day.key];
      if (dayData?.enabled && dayData?.slots?.length > 0) {
        const sortedSlots = [...dayData.slots].sort();
        
        // Group consecutive hours into ranges
        const ranges = [];
        let rangeStart = sortedSlots[0];
        let prevHour = parseInt(sortedSlots[0].split(':')[0]);
        
        for (let i = 1; i <= sortedSlots.length; i++) {
          const currentSlot = sortedSlots[i];
          const currentHour = currentSlot ? parseInt(currentSlot.split(':')[0]) : -1;
          
          // Check if current hour is NOT consecutive (gap > 1 hour) or it's the last item
          if (currentHour !== prevHour + 1) {
            // End the current range
            const endHour = prevHour + 1;
            const formattedEnd = `${endHour.toString().padStart(2, '0')}:00`;
            ranges.push(`${rangeStart}-${formattedEnd}`);
            
            // Start a new range if there are more slots
            if (currentSlot) {
              rangeStart = currentSlot;
            }
          }
          prevHour = currentHour;
        }
        
        workingDays.push({
          day: day.label,
          dayShort: day.label.slice(0, 3),
          range: ranges.join(', '),
          slotsCount: dayData.slots.length
        });
      }
    });
    return workingDays;
  };

  const workingHoursPerDay = getWorkingHoursPerDay();

  return (
    <div className="space-y-6">
      {/* Header with Save Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="w-6 h-6 text-red-500" />
            Availability Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Set your weekly working hours. Clients will only see available time slots.
          </p>
        </div>
        
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`${hasChanges ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-400'} text-white`}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Unsaved Changes Alert */}
      {hasChanges && (
        <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            You have unsaved changes. Do not forget to save!
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-700">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-wrap items-start gap-4 sm:gap-6">
            {/* Stats Section */}
            <div className="flex gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-red-600 dark:text-red-400">{activeDaysCount}</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Active Days</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-orange-600 dark:text-orange-400">{totalSlotsCount}</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total Hours/Week</div>
              </div>
            </div>
            
            {/* Working Hours Per Day Section */}
            {workingHoursPerDay.length > 0 && (
              <div className="flex-1 min-w-0">
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">Working Hours</div>
                <div className="flex flex-wrap gap-2">
                  {workingHoursPerDay.map((dayInfo) => (
                    <div 
                      key={dayInfo.day}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-700 shadow-sm"
                    >
                      <span className="font-semibold text-green-700 dark:text-green-400 text-xs sm:text-sm">
                        {dayInfo.dayShort}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">:</span>
                      <span className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm">
                        {dayInfo.range}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          <strong>Tip:</strong> Leave time slots unselected for your break times. For example, if you want a lunch break from 12:00-13:00, simply do not select those hours. Your clients will only see and book the hours you have made available.
        </AlertDescription>
      </Alert>

      {/* Weekly Schedule */}
      <div className="space-y-4">
        {DAYS.map((day) => {
          const dayData = weeklySchedule[day.key] || { enabled: false, slots: [] };
          const isEnabled = dayData.enabled; // Day is enabled even if no slots selected
          const hasSlots = dayData.slots?.length > 0;
          
          return (
            <Card 
              key={day.key} 
              className={`transition-all duration-200 ${
                isEnabled 
                  ? 'border-green-200 dark:border-green-700 bg-white dark:bg-gray-800' 
                  : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
              }`}
            >
              <CardHeader className="p-4 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => toggleDay(day.key)}
                      className="data-[state=checked]:bg-green-500"
                    />
                    <div>
                      <CardTitle className={`text-base sm:text-lg ${isEnabled ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                        {day.label}
                      </CardTitle>
                      {isEnabled && (
                        <CardDescription className={`text-xs ${hasSlots ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {hasSlots ? `${dayData.slots.length} hour(s) available` : 'No hours selected'}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                  
                  {isEnabled && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => selectAllSlots(day.key)}
                        className="text-xs h-7 px-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                      >
                        Select All
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => clearAllSlots(day.key)}
                        className="text-xs h-7 px-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              {/* Time Slots Grid - Only show if day is enabled */}
              {dayData.enabled && (
                <CardContent className="p-4 pt-2">
                  <div className="grid grid-cols-6 sm:grid-cols-9 lg:grid-cols-12 gap-1 sm:gap-2">
                    {ALL_TIME_SLOTS.map((slot) => {
                      const isSelected = dayData.slots?.includes(slot);
                      return (
                        <button
                          key={slot}
                          onClick={() => toggleSlot(day.key, slot)}
                          className={`
                            py-1.5 px-1 sm:py-2 sm:px-2 rounded text-xs sm:text-sm font-medium transition-all
                            ${isSelected 
                              ? 'bg-green-500 text-white hover:bg-green-600 shadow-sm' 
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }
                          `}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Footer Info */}
      <Card className="bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="font-medium text-gray-900 dark:text-white mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Toggle a day ON to start selecting available hours</li>
                <li>Click on time slots to mark them as available</li>
                <li>Your profile card will show your working days</li>
                <li>Clients booking with you will only see your available times</li>
                <li>Already booked slots will automatically be hidden from new clients</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AvailabilityTab;
